import type { GameView } from "@numbet/engine";
import type { ClientMessage } from "@numbet/protocol";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { trackShare } from "../analytics.ts";
import { RoomStats } from "../components/setup.tsx";
import { Avatar, Footer, Logo, Page } from "../components/ui.tsx";
import { t } from "../i18n.ts";
import { RulesContent } from "../pages/Rules.tsx";

interface Props {
  code: string;
  view: GameView;
  myId: string;
  connected: Set<string>;
  send: (message: ClientMessage) => void;
}

export function Lobby({ code, view, myId, connected, send }: Props) {
  const link = `${location.origin}/${code}`;
  const [qr, setQr] = useState("");
  const [copied, setCopied] = useState(false);
  const isHost = view.hostId === myId;
  const maxPlayers = 8;

  useEffect(() => {
    QRCode.toString(link, { type: "svg", margin: 0, errorCorrectionLevel: "M", color: { dark: "#1b0b38", light: "#ffffff" } })
      .then(setQr)
      .catch(() => setQr(""));
  }, [link]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      trackShare("Link kopiert");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Zwischenablage nicht verfügbar – der Link steht ja sichtbar da
    }
  };

  const share = async () => {
    try {
      await navigator.share({ title: "numbet", text: t.joinThisRoom(code), url: link });
      trackShare("Teilen-Dialog");
    } catch {
      // abgebrochen
    }
  };

  return (
    <>
      <header className="topbar">
        <Logo />
        <span className="pill pill-pink pill-upper">{t.lobbyTitle}</span>
      </header>
      <Page>
        <div className="lobby">
          <section className="panel stack">
            <span className="label">{t.roomCode}</span>
            <div className="room-code" aria-label={code.split("").join(" ")}>
              {code.split("").map((char, i) => (
                <span key={i}>{char}</span>
              ))}
            </div>
            <p className="muted" style={{ margin: 0, fontWeight: 700 }}>
              {t.shareHint}
            </p>
            <div className="row" style={{ alignItems: "flex-start", gap: 18 }}>
              {qr && <div className="qr" aria-hidden="true" dangerouslySetInnerHTML={{ __html: qr }} />}
              <div className="stack" style={{ gap: 10, flex: 1, minWidth: 180 }}>
                <button type="button" className="btn btn-cyan" onClick={() => void copy()}>
                  {copied ? t.copied : t.copyLink}
                </button>
                {"share" in navigator && (
                  <button type="button" className="btn btn-outline" onClick={() => void share()}>
                    {t.share}
                  </button>
                )}
                <code className="small muted" style={{ wordBreak: "break-all" }}>
                  {link}
                </code>
              </div>
            </div>
          </section>

          <section className="stack">
            <div className="panel stack">
              <h2 style={{ margin: 0 }}>{t.players(view.players.length, maxPlayers)}</h2>
              <ul className="player-list">
                {view.players.map((p) => (
                  <li key={p.id}>
                    <Avatar id={p.id} name={p.name} avatar={p.avatar} />
                    <span className="who">
                      <span>
                        {p.name} {p.id === myId && <span className="muted">({t.you})</span>}
                      </span>
                      <span className="row" style={{ gap: 6 }}>
                        {p.isHost && <span className="pill pill-yellow">{t.host}</span>}
                        {!connected.has(p.id) && <span className="tag">{t.offline}</span>}
                      </span>
                    </span>
                  </li>
                ))}
                {Array.from({ length: Math.max(0, Math.min(2, maxPlayers - view.players.length)) }, (_, i) => (
                  <li key={`free-${i}`} className="empty">
                    {t.freeSeat}
                  </li>
                ))}
              </ul>
            </div>
            <RoomStats />
            {isHost ? (
              <button type="button" className="btn btn-primary btn-large btn-block" disabled={view.players.length < 2} onClick={() => send({ type: "start" })}>
                {view.players.length < 2 ? t.needPlayers : t.startGame}
              </button>
            ) : (
              <p className="panel" style={{ textAlign: "center", margin: 0, fontWeight: 800 }}>
                {t.waitingForHost}
              </p>
            )}
            <button type="button" className="btn btn-outline btn-small" onClick={() => send({ type: "leave" })}>
              {t.leaveRoom}
            </button>
          </section>

          <section style={{ gridColumn: "1 / -1" }}>
            <h2>{t.howItWorks}</h2>
            <RulesContent />
          </section>
        </div>
      </Page>
      <Footer />
    </>
  );
}
